// The game's codes as a person would say them, for the admin: a country code is its name, a round type its title on
// the TV, a mix tag what it deals. Plain functions with no React, so collection titles (here in config/) and the admin's
// previews (admin/src/previews) say things the same way. Each list names where the game says it.

const regions = new Intl.DisplayNames(["en"], { type: "region" });
const languages = new Intl.DisplayNames(["en"], { type: "language" });

export function country(code: unknown): string {
    const c = String(code ?? "").trim();
    if (!c) return "";
    if (c === "WORLD") return "Worldwide";
    try { return regions.of(c.toUpperCase()) || c; } catch { return c; }
}

export function language(code: unknown): string {
    const c = String(code ?? "").trim();
    if (!c) return "";
    try { return languages.of(c.toLowerCase()) || c; } catch { return c; }
}

// src/core/engine.ts TYPES
const ROUNDS: Record<string, string> = {
    fastest: "Fastest Finger", buzzer: "Classic Buzzer", year: "Year Hunter", snippet: "Intro Challenge", twisted: "Twisted Tunes",
    timeline: "Timeline", lyrics: "Finish the Lyric", hum: "Hum It", drop: "Joker Drop", medley: "Golden Medley",
};

// src/core/types.ts Phase: where a party game is, as the TV last told its room
const PHASES: Record<string, string> = {
    lobby: "In the lobby", loading: "Loading songs", intro: "Round intro", countdown: "Counting down", drop: "Joker Drop",
    playing: "Song playing", buzzed: "Someone buzzed", reveal: "Showing the answer", standings: "Showing the table", gameover: "Game over",
};

// songbuzz_ai_spend kind (backend/functions/songbuzz.ts)
const AI_KINDS: Record<string, string> = { tts: "Voice", comment: "Written comments", prerecord: "Recorded ahead" };

const SIGN_IN: Record<string, string> = { google: "Google", apple: "Apple", email: "Email", guest: "Guest", device: "Solo on a device, no account", deleted: "Deleted account" };
const DEVICES: Record<string, string> = { phone: "Phone", tablet: "Tablet", desktop: "Computer", tv: "TV" };
const LEVELS_OF_PLAY: Record<string, string> = { easy: "Easy", normal: "Normal", hard: "Hard" };
const LINK_KINDS: Record<string, string> = { room: "From a game (lasts the night)", link: "Shared link or QR code (7 days)" };

// The usage log's event kinds (track() in src/web and the apps). One not here is spelled out from its code.
const EVENTS: Record<string, string> = {
    page_view: "Opened a page", error: "Something broke", game_start: "Party game started", game_end: "Party game ended", song: "Song played in a party game",
    join: "Joined a game", join_error: "Couldn't join a game", invite: "Shared the game's invite", invite_join: "Joined from an invite",
    room_open: "TV opened a room", room_full: "Room was full: a phone was turned away", host_night: "First game of the night on this device",
    room_gone: "Room closed", room_unsigned: "Room had no sign-in", rematch_daily: "Asked for a Daily rematch",
    solo_start: "Solo run started", streak_lost: "Daily streak ended by a missed day", streak_saved: "Backstage Pass saved a streak", streak_pass: "Earned a Backstage Pass",
    solo_end: "Solo gig ended", solo_challenge: "Solo achievement unlocked", solo_shop: "Solo shop",
    signup_shown: "Saw the sign-in offer", signup_done: "Signed in", signup_skip: "Skipped signing in", signout: "Signed out", session_lost: "Lost their sign-in",
    consent: "Answered the cookie question", lang_switch: "Switched language", sound_on: "Turned sound on", nav_tap: "Tapped the menu",
    home_choice: "Picked from home", music_here: "Music played on this device", share_night: "Shared their night",
    outfit_open: "Opened Your look", outfit_set: "Changed their look", reward_seen: "Saw a new reward", reward_wear: "Wore a new reward", reward_skip: "Skipped a new reward",
    friends_open: "Opened Friends", friend_answer: "Answered a friend request", friend_link_share: "Shared a friend link", friend_link_add: "Added a friend by link",
    keeper_open: "Opened the score keeper", keeper_tap: "Tapped the score keeper",
    pin_shown: "Saw 'add to home screen'", pin_tap: "Tapped 'add to home screen'", pin_done: "Added to home screen", pin_no: "Said no to the home screen",
    push_on: "Turned notifications on", push_tap: "Opened a notification",
    cast_tap: "Tapped Cast", cast_start: "Started casting", cast_result: "Cast finished", cast_help: "Opened Cast help",
    tv_follow: "TV followed a room", tv_follow_tap: "Tapped follow on the TV", tv_follow_stop: "TV stopped following", tv_follow_end: "TV following ended",
    follower_tv: "A following TV joined", tv_slow: "TV switched to slow-screen mode", house_sheet: "Opened the speaker sheet",
    connection_lost: "Lost the connection", channel_refused: "Room channel refused", phone_send_stuck: "Phone couldn't send",
};
const PAGES: Record<string, string> = { home: "Home", tv: "TV", play: "Phone in a game", host: "Hosting on a phone", solo: "Solo", look: "Your look", friends: "Friends", join: "Join" };

// A mix tag (songbuzz_songs.tags, a game's music): "local:ES:2020s" is Spain's own music from the 2020s.
export function mix(tag: unknown): string {
    const t = String(tag ?? "").trim();
    let m = /^local:([A-Z]{2}):(.+)$/.exec(t);
    if (m) return m[2] === "all" ? `${country(m[1])}'s own music` : `${country(m[1])}'s own, ${m[2]}`;
    if ((m = /^known:([A-Z]{2})$/.exec(t))) return `Known in ${country(m[1])}`;
    if ((m = /^lang:([a-z]{2,3})$/.exec(t))) return `Sung in ${language(m[1])}`;
    if ((m = /^chart:([A-Z]{2})$/.exec(t))) return `${country(m[1])}'s charts`;
    return t;
}

// A chart a song made (songbuzz_songs.charts): "ES 1987 no1" is number 1 in Spain in 1987
export function chart(entry: unknown): string {
    const m = /^([A-Z]{2}) (\d{4}) (.+)$/.exec(String(entry ?? ""));
    if (!m) return String(entry ?? "");
    const place = m[3] === "no1" ? "number 1" : m[3].replace(/^top(\d+)$/, "top $1");
    return `${country(m[1])} ${m[2]}, ${place}`;
}

// A catalogue file (catalogueFileName in backend/functions/songbuzz.ts): "world/lang_3apt.json" is the worldwide file of
// songs sung in Portuguese, "local/ES/all.json" Spain's own music.
export function catalogueFile(path: unknown): string {
    const p = String(path ?? "");
    if (p === "songs.json") return "Every song";
    const unsafe = (x: string) => x.replace(/\.json$/, "").replace(/_([0-9a-f]{2})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
    const parts = p.split("/");
    if (parts[0] === "local" && parts.length === 3) return mix(`local:${parts[1]}:${unsafe(parts[2])}`);
    if (parts.length === 2) return `${mix(unsafe(parts[1]))} · ${parts[0] === "world" ? "worldwide" : `for ${country(parts[0])}`}`;
    return p;
}

// "2026-11-03" as "Tue 3 Nov 2026"
export function day(value: unknown): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
    if (!m) return String(value ?? "");
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

// A moment in the viewer's own time, the day first: "Today 19:07", "Yesterday 23:16", "Sun 2 Nov 21:14", and the year
// once it isn't this one
export function moment(value: unknown): string {
    const d = value instanceof Date ? value : new Date(String(value ?? ""));
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const now = new Date();
    const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
    if (days === 0) return `Today ${time}`;
    if (days === 1) return `Yesterday ${time}`;
    const date = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
    return `${date} ${time}`;
}

// "solo_end" as "Solo end", "bestNight" as "Best night": for a code nobody named
export const spell = (code: unknown) => {
    const s = String(code ?? "").replace(/[_:]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim().toLowerCase();
    return s ? s[0].toUpperCase() + s.slice(1) : "";
};

const table = (t: Record<string, string>) => (x: unknown) => t[String(x ?? "")] ?? spell(x);

// What Words (the preview) can say, by the name a property gives in customProps.
export const SAY: Record<string, (x: unknown) => string> = {
    country, language, mix, day, catalogueFile, chart,
    round: table(ROUNDS), phase: table(PHASES), aiKind: table(AI_KINDS), signIn: table(SIGN_IN), device: table(DEVICES),
    level: table(LEVELS_OF_PLAY), linkKind: table(LINK_KINDS), event: table(EVENTS), page: table(PAGES),
    decade: (x) => (x == null || x === "" ? "" : `${x}s`),
};
